import { useEffect, useRef, useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  BookOpen,
  FileText,
  Shield,
  UserCheck,
  Wallet,
  BarChart3,
  Layers,
  Package,
  AlertTriangle,
  Gavel,
  Bookmark,
  ChevronRight,
} from "lucide-react";

export const Route = createFileRoute("/manual")({ component: ManualPage });

// ---------------------------------------------------------------------------
// Table of Contents
// ---------------------------------------------------------------------------

interface TocItem {
  id: string;
  label: string;
  level: 1 | 2;
}

const TOC: TocItem[] = [
  { id: "section-1", label: "1. General Policies and Scope", level: 1 },
  { id: "section-1-1", label: "1.1 Purpose and Scope", level: 2 },
  { id: "section-1-2", label: "1.2 Regulatory Compliance", level: 2 },
  { id: "section-1-3", label: "1.3 Credit Philosophy", level: 2 },
  { id: "section-1-4", label: "1.4 Ethical Standards and Bias Controls", level: 2 },

  { id: "section-2", label: "2. Application Intake", level: 1 },
  { id: "section-2-1", label: "2.1 Application Receipt and SLA", level: 2 },
  { id: "section-2-2", label: "2.2 Channel Priority", level: 2 },
  { id: "section-2-3", label: "2.3 Required Documentation by Product", level: 2 },
  { id: "section-2-4", label: "2.4 Document Completeness and needs_info", level: 2 },
  { id: "section-2-5", label: "2.5 Appeal and Reinstatement", level: 2 },

  { id: "section-3", label: "3. Identity and Residency", level: 1 },
  { id: "section-3-1", label: "3.1 Identity Verification", level: 2 },
  { id: "section-3-2", label: "3.2 Digital vs. Branch Channels", level: 2 },
  { id: "section-3-3", label: "3.3 Address Verification", level: 2 },
  { id: "section-3-4", label: "3.4 Sanctions and PEP Screening", level: 2 },

  { id: "section-4", label: "4. Income and Employment", level: 1 },
  { id: "section-4-1", label: "4.1 Income Evidence Requirements", level: 2 },
  { id: "section-4-2", label: "4.2 Bank-deposit Cross-check", level: 2 },
  { id: "section-4-3", label: "4.3 Self-employed and Commission Workers", level: 2 },
  { id: "section-4-4", label: "4.4 Employment Tenure Requirements", level: 2 },
  { id: "section-4-5", label: "4.5 Foreign Income Treatment", level: 2 },

  { id: "section-5", label: "5. Credit Bureau Assessment", level: 1 },
  { id: "section-5-1", label: "5.1 Bureau Selection and Freshness", level: 2 },
  { id: "section-5-2", label: "5.2 Score Interpretation", level: 2 },
  { id: "section-5-3", label: "5.3 Tradelines, Delinquencies, Utilization", level: 2 },
  { id: "section-5-4", label: "5.4 Adverse Events and Collections", level: 2 },
  { id: "section-5-5", label: "5.5 Thin-file Applicants", level: 2 },

  { id: "section-6", label: "6. Affordability and DTI", level: 1 },
  { id: "section-6-1", label: "6.1 DTI Methodology", level: 2 },
  { id: "section-6-2", label: "6.2 DTI Bands and Actions", level: 2 },
  { id: "section-6-3", label: "6.3 Stress Testing", level: 2 },
  { id: "section-6-4", label: "6.4 Monthly Disposable Income Floor", level: 2 },
  { id: "section-6-5", label: "6.5 Student Loans and Revolving Credit", level: 2 },

  { id: "section-7", label: "7. Risk Tiering", level: 1 },
  { id: "section-7-1", label: "7.1 Tier Definitions A–D", level: 2 },
  { id: "section-7-2", label: "7.2 Composite Scoring", level: 2 },
  { id: "section-7-3", label: "7.3 Tier Drift", level: 2 },
  { id: "section-7-4", label: "7.4 Override Procedures", level: 2 },

  { id: "section-8", label: "8. Product-specific Policies", level: 1 },
  { id: "section-8-1", label: "8.1 Amount Caps by Tier", level: 2 },
  { id: "section-8-2", label: "8.2 Personal Loans", level: 2 },
  { id: "section-8-3", label: "8.3 Credit Cards", level: 2 },
  { id: "section-8-4", label: "8.4 Auto Loans", level: 2 },
  { id: "section-8-5", label: "8.5 Mortgages and HELOCs", level: 2 },

  { id: "section-9", label: "9. Fraud Screening", level: 1 },
  { id: "section-9-1", label: "9.1 Fraud Signal Taxonomy", level: 2 },
  { id: "section-9-2", label: "9.2 Device Fingerprinting", level: 2 },
  { id: "section-9-3", label: "9.3 Velocity and Known-pattern Rules", level: 2 },
  { id: "section-9-4", label: "9.4 Fraud Hold Workflow", level: 2 },

  { id: "section-10", label: "10. Decisioning, HITL, and Audit", level: 1 },
  { id: "section-10-1", label: "10.1 Auto-approve Criteria", level: 2 },
  { id: "section-10-2", label: "10.2 Auto-decline Criteria", level: 2 },
  { id: "section-10-3", label: "10.3 Human-in-the-loop", level: 2 },
  { id: "section-10-4", label: "10.4 Officer Discretion", level: 2 },
  { id: "section-10-5", label: "10.5 Audit Trail", level: 2 },
  { id: "section-10-6", label: "10.6 Quality Assurance", level: 2 },
  { id: "section-10-7", label: "Appendix A: Glossary", level: 2 },
  { id: "section-10-8", label: "Appendix B: Escalation Contacts", level: 2 },
];

const sectionIcons: Record<string, React.ReactNode> = {
  "section-1": <BookOpen className="h-3.5 w-3.5" />,
  "section-2": <FileText className="h-3.5 w-3.5" />,
  "section-3": <UserCheck className="h-3.5 w-3.5" />,
  "section-4": <Wallet className="h-3.5 w-3.5" />,
  "section-5": <BarChart3 className="h-3.5 w-3.5" />,
  "section-6": <BarChart3 className="h-3.5 w-3.5" />,
  "section-7": <Layers className="h-3.5 w-3.5" />,
  "section-8": <Package className="h-3.5 w-3.5" />,
  "section-9": <AlertTriangle className="h-3.5 w-3.5" />,
  "section-10": <Gavel className="h-3.5 w-3.5" />,
};

// ---------------------------------------------------------------------------
// Reusable primitives
// ---------------------------------------------------------------------------

function Callout({
  type,
  children,
}: {
  type: "important" | "note" | "warning";
  children: React.ReactNode;
}) {
  const styles: Record<string, string> = {
    important: "border-brand bg-brand/10 text-foreground",
    note: "border-success bg-success/10 text-foreground",
    warning: "border-warn bg-warn/10 text-foreground",
  };
  const labels = { important: "IMPORTANT", note: "NOTE", warning: "WARNING" };
  return (
    <div className={`my-4 border-l-4 px-4 py-3 text-xs rounded-r ${styles[type]}`}>
      <span className="font-bold uppercase tracking-wide">{labels[type]}:</span>{" "}
      {children}
    </div>
  );
}

function SectionHeading({
  id,
  children,
  level = 2,
  anchor,
}: {
  id: string;
  children: React.ReactNode;
  level?: 2 | 3;
  anchor?: string;
}) {
  const Tag = level === 2 ? "h2" : "h3";
  const cls =
    level === 2
      ? "text-lg font-bold mt-10 mb-3 pb-2 border-b border-border scroll-mt-6 flex items-center gap-2"
      : "text-base font-semibold mt-8 mb-2 scroll-mt-6 flex items-center gap-2";
  return (
    <Tag id={id} className={cls}>
      <span>{children}</span>
      {anchor && (
        <span className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[0.65rem] font-mono font-normal text-muted-foreground tracking-normal">
          {anchor}
        </span>
      )}
    </Tag>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-sm leading-relaxed mb-3">{children}</p>;
}

function Th({ children, center }: { children: React.ReactNode; center?: boolean }) {
  return (
    <th className={`border border-border px-3 py-2 font-semibold ${center ? "text-center" : "text-left"}`}>
      {children}
    </th>
  );
}

function Td({ children, center, mono }: { children: React.ReactNode; center?: boolean; mono?: boolean }) {
  return (
    <td className={`border border-border px-3 py-1.5 ${center ? "text-center" : ""} ${mono ? "font-mono text-[0.72rem]" : ""}`}>
      {children}
    </td>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ManualPage() {
  const [activeId, setActiveId] = useState<string>("");
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleClick = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const ids = TOC.map((t) => t.id);
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const sorted = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(sorted[0].target.id);
        }
      },
      { rootMargin: "-10% 0px -80% 0px", threshold: 0 },
    );
    elements.forEach((el) => observerRef.current!.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="flex gap-8">
      {/* Table of contents */}
      <aside className="sticky top-4 h-[calc(100vh-4rem)] w-[260px] shrink-0 overflow-y-auto rounded-lg border border-border bg-muted/20 py-3">
        <div className="mb-2 flex items-center gap-2 border-b border-border px-4 pb-2">
          <Shield className="h-4 w-4 text-brand" />
          <span className="text-[0.7rem] font-bold uppercase tracking-wider text-brand">
            Table of Contents
          </span>
        </div>
        <nav className="space-y-0.5 px-2">
          {TOC.map((item) => {
            const isActive = activeId === item.id;
            const isTopLevel = item.level === 1;
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item.id)}
                className={`flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs transition-colors ${
                  isTopLevel ? "font-semibold" : "pl-6 font-normal"
                } ${
                  isActive
                    ? "bg-brand/10 text-brand"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {isTopLevel && sectionIcons[item.id] ? (
                  sectionIcons[item.id]
                ) : !isTopLevel ? (
                  <ChevronRight className="h-2.5 w-2.5 opacity-40" />
                ) : null}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <article className="min-w-0 flex-1 pb-16 max-w-5xl">
        {/* Document header */}
        <div className="mb-8 border-b-2 border-brand pb-6">
          <div className="mb-1 flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-brand" strokeWidth={1.9} />
            <span className="text-[0.7rem] font-bold uppercase tracking-wider text-brand">
              Credit Policy Manual
            </span>
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight">
            Retail Banking Credit Policy and Operating Procedures
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Consumer unsecured &amp; secured lending &mdash; all distribution channels
          </p>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span><strong>Document ID:</strong> CP-MANUAL-2026-007</span>
            <span><strong>Version:</strong> 11.4</span>
            <span><strong>Effective Date:</strong> February 1, 2026</span>
            <span><strong>Last Revised:</strong> April 12, 2026</span>
            <span><strong>Classification:</strong> CONFIDENTIAL &mdash; Internal Use Only</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span><strong>Approved By:</strong> Rebecca M. Alvarez &mdash; Chief Credit Officer</span>
            <span><strong>Reviewed By:</strong> Compliance, Legal, Risk, Operations</span>
          </div>
        </div>

        <Callout type="important">
          This manual supersedes all prior versions. Credit officers, underwriters, and
          decision agents must comply with every guideline herein effective February 1, 2026.
          Deviations require documented discretion per Section 10.4. Unauthorised
          departures may expose the institution to regulatory sanctions and personal
          disciplinary action.
        </Callout>

        {/* ================================================================== */}
        {/* SECTION 1 — General Policies and Scope */}
        {/* ================================================================== */}
        <SectionHeading id="section-1">1. General Policies and Scope</SectionHeading>

        <SectionHeading id="section-1-1" level={3}>1.1 Purpose and Scope</SectionHeading>
        <P>
          This manual establishes the credit-underwriting standards, operating
          procedures, and decisioning rules applicable to all retail-banking credit
          products issued by the Bank across every distribution channel &mdash; branch,
          telephone, digital portal, broker referral, and embedded partner. The manual
          governs applications for personal loans, credit cards, auto loans, residential
          mortgages, HELOCs, and overdraft facilities originated on or after the
          effective date.
        </P>
        <P>
          The scope of this manual covers individual consumer applicants aged 18 through
          75, resident in jurisdictions in which the Bank holds a retail lending
          licence. Small-business, corporate, commercial real-estate, and specialty
          wholesale credit lines are governed by the Commercial Credit Manual
          (CC-2026-003) and are explicitly out of scope here. Co-applicant and joint
          applications are governed by Supplemental Bulletin JOINT-2026.
        </P>
        <P>
          All Bank employees with credit-authority &mdash; including credit officers,
          senior underwriters, automated decisioning systems, and agentic AI systems
          acting under delegated authority &mdash; are bound by this manual. Agents
          within the BAIC Agentic Layer operate under the same policy envelope as human
          officers and are subject to the same audit trail requirements (Section 10.5).
        </P>

        <SectionHeading id="section-1-2" level={3}>1.2 Regulatory Compliance</SectionHeading>
        <P>
          All credit decisions must comply with applicable federal and state
          regulations, including but not limited to the Equal Credit Opportunity Act
          (ECOA), the Fair Credit Reporting Act (FCRA), the Truth in Lending Act (TILA),
          Regulation B, Regulation Z, the Fair Housing Act, state unfair-trade-practices
          acts, the Consumer Financial Protection Bureau's Unfair, Deceptive, or
          Abusive Acts or Practices (UDAAP) guidance, and all applicable anti-money
          laundering (AML) and know-your-customer (KYC) rules.
        </P>
        <P>
          Under ECOA and Regulation B, adverse-action notices must be issued within 30
          calendar days of application for declined or counter-offered applications, and
          must include the specific principal reasons for the adverse action. Officers
          may not use protected characteristics (race, colour, religion, national
          origin, sex, marital status, age, receipt of public assistance, or good-faith
          exercise of rights under consumer-credit laws) as a basis for any
          credit-related decision.
        </P>
        <Callout type="warning">
          Effective July 1, 2025, several states (CA, CO, IL, NY, WA) require bias and
          impact assessments for automated credit-decisioning models, including agentic
          AI systems. Every HITL override of an algorithmic recommendation must record
          the specific rationale so the Bank can produce the assessments on demand. See
          Compliance Bulletin CB-2025-09 for full requirements.
        </Callout>
        <P>
          Adverse-credit events sourced from consumer reporting agencies are subject to
          FCRA dispute rights. When a credit decision relies in whole or in part on a
          consumer report, the adverse-action notice must identify the reporting agency
          and inform the applicant of the right to obtain a free copy of their report.
          When the decision relies on information from a source other than the
          applicant or a consumer reporting agency (for example, internal bank records,
          open-banking-sourced deposits, or Bank-operated device-fingerprint
          databases), the applicant must be informed of the right to request the nature
          of that information within 60 days.
        </P>

        <SectionHeading id="section-1-3" level={3}>1.3 Credit Philosophy</SectionHeading>
        <P>
          The Bank's credit philosophy is to extend credit to applicants whose
          documented ability and willingness to repay meets the Bank's risk
          thresholds, priced to reflect the specific risk of the individual applicant
          and the chosen product. Decisions must reflect the totality of the
          applicant's profile &mdash; identity, income, affordability, credit history,
          collateral (where applicable), and fraud signals &mdash; not any single
          isolated factor.
        </P>
        <P>
          The Bank employs a three-line-of-defence model. The first line (credit
          officers and agentic systems under delegated authority) makes the initial
          decision. The second line (Credit Risk Management) sets policy, monitors
          vintage performance, and sets tier thresholds. The third line (Internal
          Audit) independently tests the effectiveness of credit controls and samples
          individual decisions quarterly.
        </P>
        <P>
          The Bank's target 24-month vintage loss rate for unsecured consumer lending
          is 1.8&ndash;2.4% of drawn balances. Credit standards are calibrated to meet
          this target while maintaining competitive approval rates across all tiers.
          The Credit Risk Management Committee (CRMC) reviews vintage performance
          monthly and issues guideline adjustments via Credit Risk Bulletins (CRB-YYYY-NN)
          which must be applied promptly upon issuance.
        </P>

        <SectionHeading id="section-1-4" level={3}>1.4 Ethical Standards and Bias Controls</SectionHeading>
        <P>
          Credit officers, underwriters, and reviewers must adhere to the highest
          ethical standards. Decisions must be made objectively, grounded in documented
          evidence and the policies herein. Officers must recuse themselves from any
          application involving a personal relationship with the applicant, intermediary,
          or beneficiary, and escalate to the Head of Underwriting for reassignment.
        </P>
        <P>
          Automated and agentic decisioning systems are subject to the same fairness
          standards as human officers. Every automated decision must be traceable to a
          specific policy rule or set of rules. Agentic AI systems must emit structured
          reasoning that can be audited post-hoc; opaque “black-box” recommendations
          are not permitted to act as the sole basis for a decision. This manual is the
          authoritative source of policy for both human and agentic decisioning.
        </P>
        <Callout type="note">
          Annual fair-lending training and the Bank's implicit-bias certification are
          mandatory for all officers with credit authority. Completion must be
          recorded by December 31 of each calendar year. Agentic decision systems are
          subject to the CRMC's quarterly bias-impact review in lieu of human training.
        </Callout>

        {/* ================================================================== */}
        {/* SECTION 2 — Application Intake */}
        {/* ================================================================== */}
        <SectionHeading id="section-2">2. Application Intake</SectionHeading>

        <SectionHeading id="section-2-1" level={3}>2.1 Application Receipt and SLA</SectionHeading>
        <P>
          All applications are time-stamped at the moment of submission. Applications
          submitted through the Bank's digital portal or embedded partner channel are
          automatically logged and assigned a case ID. Branch applications, telephone
          captures, and broker referrals must be entered into the New Business Queue
          (NBQ) within one business day of the customer's signature.
        </P>
        <P>
          The Bank operates to the following service-level targets, measured from
          receipt of a complete application:
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Product / Amount</Th>
              <Th>Triage SLA</Th>
              <Th>Requirements Ordered</Th>
              <Th>Decision Target</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Credit Card (any limit)</Td>
              <Td>Same day</Td>
              <Td>Bureau, fraud, income (if stated)</Td>
              <Td>2 hours</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Personal Loan (&le;$25K)</Td>
              <Td>Same day</Td>
              <Td>Bureau, fraud, income, affordability</Td>
              <Td>24 hours</Td>
            </tr>
            <tr>
              <Td>Personal Loan ($25K&ndash;$75K)</Td>
              <Td>Same day</Td>
              <Td>Full suite + bank-deposit verification</Td>
              <Td>3 business days</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Auto Loan (any)</Td>
              <Td>Same day</Td>
              <Td>Full suite + collateral valuation</Td>
              <Td>3 business days</Td>
            </tr>
            <tr>
              <Td>Mortgage / HELOC</Td>
              <Td>1 business day</Td>
              <Td>Full suite + appraisal + title</Td>
              <Td>15 business days</Td>
            </tr>
          </tbody>
        </table>
        <P>
          Applications are routed to the appropriate decisioning lane based on product
          and amount. Credit cards and small personal loans are handled by the
          automated agentic lane under the Decision Agent's DECISION.AUTO authority
          (Section 10.1). Personal loans above $25,000, all auto loans, and all
          secured-credit products are routed to a human credit officer regardless of
          recommendation strength.
        </P>

        <SectionHeading id="section-2-2" level={3}>2.2 Channel Priority</SectionHeading>
        <P>
          Where an applicant submits through multiple channels simultaneously, the
          Bank treats the earliest time-stamped application as canonical and closes
          duplicates with a reference to the primary case. Channel conflicts must be
          reconciled within one business day. An applicant submitting different amounts
          or products through multiple channels within a 30-day window is presumed to
          be rate-shopping; the Bank will treat this as a single inquiry event for
          bureau-pull purposes where the law permits.
        </P>
        <P>
          Broker-originated applications are subject to the same policies as
          branch-originated applications. The broker's compensation structure may not
          influence any credit-decision factor; officers must review broker-originated
          applications with equal rigor and may not “stretch” for approval under
          volume pressure.
        </P>

        <SectionHeading id="section-2-3" level={3}>2.3 Required Documentation by Product</SectionHeading>
        <P>
          Document requirements depend on product and requested amount. The following
          matrix is cumulative &mdash; each tier includes all requirements from smaller
          amounts.
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Document kind / accepted documents</Th>
              <Th center>Card &le;$10K</Th>
              <Th center>PL &le;$25K</Th>
              <Th center>PL $25–75K</Th>
              <Th center>Auto</Th>
              <Th center>Mortgage</Th>
            </tr>
          </thead>
          <tbody>
            {[
              {
                kind: "Application form",
                docs: "Signed Part 1 application (digital signature accepted)",
                cells: ["R", "R", "R", "R", "R"],
              },
              {
                kind: "Government-issued photo ID",
                docs: "Passport · Driving licence · National ID card (issued or re-verified within 90 days)",
                cells: ["R", "R", "R", "R", "R"],
              },
              {
                kind: "Proof of address",
                docs: "Utility bill ≤90 days · Tenancy agreement · Bank/credit-card statement · Employer letter on letterhead",
                cells: ["R", "R", "R", "R", "R"],
              },
              {
                kind: "Identity verification record",
                docs: "Digital IDV provider attestation with liveness + document-authenticity check, or In-branch KYC log entry",
                cells: ["R", "R", "R", "R", "R"],
              },
              {
                kind: "Pay stubs",
                docs: "3 most recent monthly pay stubs (or 2 if monthly-paid); must show gross, deductions, net, employer, tax ID, pay-period end",
                cells: ["—", "R", "R", "R", "R"],
              },
              {
                kind: "Bank statements",
                docs: "60–90 days of primary deposit-account statements; Open Banking feed accepted in lieu",
                cells: ["—", "R", "R", "R", "R"],
              },
              {
                kind: "Employer confirmation",
                docs: "Letter on employer letterhead signed by HR / payroll officer, dated within 30 days",
                cells: ["—", "C", "R", "R", "R"],
              },
              {
                kind: "Tax returns",
                docs: "Most recent 2 filed returns (self-assessment or equivalent) + YTD P&L for self-employed applicants",
                cells: ["—", "—", "C", "C", "R"],
              },
              {
                kind: "Collateral details",
                docs: "Vehicle: VIN + dealer invoice + KBB retail value · Property: address, purchase contract, down-payment evidence",
                cells: ["—", "—", "—", "R", "R"],
              },
              {
                kind: "Property appraisal",
                docs: "Independent appraisal report from a Bank-approved valuer, dated within 120 days of decision",
                cells: ["—", "—", "—", "—", "R"],
              },
              {
                kind: "Title & insurance",
                docs: "Title search report · Comprehensive-and-collision (auto) or homeowner's (property) insurance certificate naming the Bank as loss payee",
                cells: ["—", "—", "—", "C", "R"],
              },
              {
                kind: "HMDA disclosure",
                docs: "Home Mortgage Disclosure Act loan/application register (LAR) demographic disclosure form",
                cells: ["—", "—", "—", "—", "R"],
              },
            ].map((row, i) => (
              <tr key={i} className={i % 2 === 1 ? "bg-muted/50" : ""}>
                <Td>
                  <div className="font-semibold text-foreground">{row.kind}</div>
                  <div className="mt-0.5 text-[0.7rem] leading-snug text-muted-foreground">
                    {row.docs}
                  </div>
                </Td>
                {row.cells.map((cell, j) => (
                  <Td key={j} center>
                    {cell === "R" ? (
                      <span className="text-brand font-bold">R</span>
                    ) : cell === "C" ? (
                      <span className="text-warn font-semibold">C</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </Td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mb-4">
          <strong>R</strong> = Required &nbsp;|&nbsp;
          <strong>C</strong> = Conditional (required if triggered by findings) &nbsp;|&nbsp;
          <strong>—</strong> = Not required at this product/amount
        </p>

        <SectionHeading id="section-2-4" level={3} anchor="POLICY.DOCUMENTS">
          2.4 Document Completeness and needs_info
        </SectionHeading>
        <P>
          Missing or mismatched mandatory documents route the application to
          <code className="mx-1 rounded bg-muted px-1 text-[0.72rem]">needs_info</code>
          status. The credit officer (or Decision Agent) must issue a
          document-request notice specifying exactly which items are missing,
          providing clear instructions for fulfilment, and including the deadline for
          response.
        </P>
        <P>
          The standard outstanding-requirement follow-up schedule is: initial request
          at day 0, first reminder at day 7, second reminder at day 14, final notice
          at day 21, and case closure at day 30. All communications must be recorded
          in the audit trail (Section 10.5). If the application is closed for
          non-response, the applicant may submit a new application at any time; a
          fresh bureau pull and fresh income evidence will be required.
        </P>
        <Callout type="note">
          Document-mismatch cases &mdash; where a document is present but its content
          diverges from the Part 1 application (for example, stated income differs
          materially from the gross pay-stub total) &mdash; are treated as <em>needs_info</em>
          rather than auto-decline. The officer requests clarifying documents before
          any adverse decision.
        </Callout>

        <SectionHeading id="section-2-5" level={3}>2.5 Appeal and Reinstatement</SectionHeading>
        <P>
          Declined applicants may request a reconsideration within 30 days of the
          adverse-action notice. Reconsideration requests must be reviewed by an
          officer at least one authority level above the officer who made the original
          decision. New information may be submitted; no new bureau pull is made
          unless more than 90 days have elapsed since the original.
        </P>
        <P>
          A closed <em>needs_info</em> case may be re-opened within 60 days of closure
          without a new application, provided the applicant supplies the outstanding
          documents and there has been no material change in circumstances. Beyond 60
          days a full new application and fresh bureau pull are required.
        </P>

        {/* ================================================================== */}
        {/* SECTION 3 — Identity and Residency */}
        {/* ================================================================== */}
        <SectionHeading id="section-3">3. Identity and Residency</SectionHeading>

        <SectionHeading id="section-3-1" level={3} anchor="INTAKE.IDENTITY">
          3.1 Identity Verification
        </SectionHeading>
        <P>
          A complete application must include a government-issued photo ID &mdash;
          passport, driving licence, or national ID card &mdash; uploaded within the
          last 90 days, together with confirmation that the applicant has been
          identified via either a digital channel (an approved IDV provider) or in
          branch by a Bank employee. The identity evidence on the application must
          match the customer record on file; any mismatch must be surfaced as a
          warning and resolved before a Decision can be rendered.
        </P>
        <P>
          The Identity Check Agent (or equivalent officer review) compares at minimum
          the following fields between the submitted ID and the customer record: full
          name, date of birth, photo-hash (if available), ID number, and issue date.
          The digital-ID provider must have completed a liveness check and a
          document-authenticity check within the last 30 days. A digital-ID completion
          older than 30 days requires re-verification.
        </P>
        <P>
          In-branch identification must be performed by a Bank employee trained in KYC
          procedures. The employee records their ID verification in the KYC log along
          with the applicant's signature, the document presented, and the verification
          timestamp. Branch-identified applications are eligible for every credit
          product on the same basis as digitally-identified applications.
        </P>

        <SectionHeading id="section-3-2" level={3}>3.2 Digital vs. Branch Channels</SectionHeading>
        <P>
          The Bank accepts both digital and in-branch identification as equivalent for
          credit-decisioning purposes. However, the two channels have different
          operational implications:
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Attribute</Th>
              <Th>Digital IDV</Th>
              <Th>In-branch</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Median time-to-verify</Td>
              <Td>&lt; 3 minutes</Td>
              <Td>15–25 minutes</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Re-verification required after</Td>
              <Td>30 days</Td>
              <Td>180 days</Td>
            </tr>
            <tr>
              <Td>Eligible for auto-approve pathway</Td>
              <Td>Yes</Td>
              <Td>Yes</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Supporting evidence stored</Td>
              <Td>Liveness video, document scan</Td>
              <Td>Photo-copy, KYC log entry</Td>
            </tr>
          </tbody>
        </table>

        <SectionHeading id="section-3-3" level={3} anchor="INTAKE.ADDRESS">
          3.3 Address Verification
        </SectionHeading>
        <P>
          The applicant's stated address on file must be corroborated by at least one
          of the following documents: a utility bill dated within the last 90 days, a
          current tenancy agreement, a bank or credit-card statement showing the
          applicant's name and address, or an employer letter on letterhead. The
          Address Verification Agent parses the supporting document, normalises the
          address to the Bank's canonical address format, and records a match or
          mismatch result.
        </P>
        <P>
          Address mismatches are treated as follows: if the mismatch is a minor
          formatting difference (for example, apartment number omitted from the
          customer record but present on the utility bill) the agent records a <em>match</em>
          result with a note. If the mismatch is structural (different street, city, or
          zip/postcode), the application is routed to HITL for officer review. Officers
          may accept a mismatch where the applicant provides a satisfactory explanation
          (recent move, dual residences, student at a different address); any
          acceptance must be recorded in the audit log.
        </P>

        <SectionHeading id="section-3-4" level={3}>3.4 Sanctions and PEP Screening</SectionHeading>
        <P>
          Every application is screened against the Office of Foreign Assets Control
          (OFAC) Specially Designated Nationals list, the United Nations Consolidated
          Sanctions List, the EU sanctions list, and the Bank's internal deny-list.
          Screening occurs at application receipt and is re-run at the point of
          decision. A hard hit against any sanctions list results in immediate case
          suspension and referral to the Financial Crimes Compliance team; no
          credit-decision fields may be communicated to the applicant pending FCC
          review.
        </P>
        <P>
          Politically Exposed Persons (PEPs) and their immediate family members are
          subject to enhanced due diligence. PEP-flagged applications are routed to
          HITL regardless of other signals, and require sign-off from the Head of
          Compliance before any credit decision is rendered. PEP status alone is not a
          basis for decline; however, the underlying source-of-funds review is
          mandatory.
        </P>

        {/* ================================================================== */}
        {/* SECTION 4 — Income and Employment */}
        {/* ================================================================== */}
        <SectionHeading id="section-4">4. Income and Employment</SectionHeading>

        <SectionHeading id="section-4-1" level={3} anchor="INTAKE.INCOME">
          4.1 Income Evidence Requirements
        </SectionHeading>
        <P>
          Stated income must be supported by (1) the three most recent pay stubs or
          equivalent employer statement, and (2) at least 60 days of bank-account
          statements showing matching net-deposit activity. Where the applicant is
          paid monthly rather than bi-weekly, the Bank accepts the two most recent
          pay stubs in lieu of three. Pay stubs must show gross pay, deductions, net
          pay, employer name, employer tax identifier, and the pay-period end date.
        </P>
        <P>
          The Income Verification Agent cross-checks pay-stub gross monthly income
          against bank-deposit activity. A variance of more than 15% triggers a
          warning, and a variance of more than 25% requires HITL review. The officer
          must reconcile the two figures and document the conclusion. Common
          legitimate reasons for variance include: variable-commission components, bonus
          payments, recent raise not yet reflected in the deposit window, direct
          deposits to non-primary accounts, and cash tips that do not flow through the
          observed account.
        </P>
        <Callout type="important">
          For agentic decisioning, the verified income used by the Affordability Agent
          must be the <em>lower</em> of the stated income and the bank-deposit-derived
          income, unless an officer has documented a specific exception. This
          conservative treatment protects the Bank from over-stated income inflating
          affordability calculations.
        </Callout>

        <SectionHeading id="section-4-2" level={3}>4.2 Bank-deposit Cross-check Methodology</SectionHeading>
        <P>
          The Bank computes rolling monthly net deposits from the applicant's primary
          deposit account over a 90-day window. “Net deposits” exclude transfers from
          other accounts held by the same applicant, returned items, and
          re-credits of fraudulent transactions. Where the applicant grants Open
          Banking access, the Bank uses aggregated deposit data from all linked
          accounts. Where Open Banking access is declined, the Bank relies solely on
          statements of accounts held at the Bank; external-account statements
          supplied by the applicant are accepted but rated as a lower evidence tier.
        </P>
        <P>
          The methodology is:
        </P>
        <ol className="list-decimal list-inside text-sm mb-3 space-y-1">
          <li>Exclude all intra-applicant transfers and returned items.</li>
          <li>
            Classify remaining credits as “payroll-consistent” (recurring on a
            regular schedule, matching employer name) or “other” (one-off credits,
            refunds, gifts).
          </li>
          <li>Sum payroll-consistent credits over 90 days; divide by three to get a monthly average.</li>
          <li>
            Compare to stated income; compute variance as
            <code className="mx-1 rounded bg-muted px-1 text-[0.72rem]">(stated &minus; observed) / stated &times; 100</code>.
          </li>
          <li>Record both figures and the variance on the case.</li>
        </ol>

        <SectionHeading id="section-4-3" level={3}>4.3 Self-employed and Commission Workers</SectionHeading>
        <P>
          Self-employed applicants, sole traders, freelancers, and commission-based
          sales workers are evaluated on a different evidence standard. These
          applicants must provide two years of filed tax returns (self-assessment or
          equivalent), a year-to-date profit-and-loss statement signed by the
          applicant or their accountant, and bank statements covering the most recent
          180 days rather than the standard 60.
        </P>
        <P>
          Income for self-employed applicants is computed as the average of the two
          most recent years' net profit, or the most recent year's net profit,
          whichever is lower. Where year-over-year profit has declined by more than
          20%, the officer must apply the current-year figure only, and document the
          rationale. Commission-based workers with variable earnings must show at
          least 24 months of commission history; their qualifying income is the
          average of the past two years.
        </P>

        <SectionHeading id="section-4-4" level={3} anchor="POLICY.TENURE">
          4.4 Employment Tenure Requirements
        </SectionHeading>
        <P>
          Applicants below one year of current-employer tenure are capped at Tier C
          regardless of other signals. Officer discretion may waive this cap in the
          following specific circumstances, subject to documented justification:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>
            Strong bureau score (&ge;&nbsp;720) combined with continuous employment
            history showing no unexplained gaps in the prior 36 months.
          </li>
          <li>
            Promotion or internal transfer within the same employer (treated as
            continuous tenure).
          </li>
          <li>
            Inter-company transfer between parent and subsidiary within a defined
            corporate group.
          </li>
          <li>
            Recent industry transition where the applicant's new role is documented
            as a promotion in responsibility or compensation.
          </li>
        </ul>
        <P>
          Multiple concurrent positions (gig economy, dual employment) count toward
          tenure at the longer-held position. Recent job loss, unemployment gaps, or
          pending termination must be disclosed; failure to disclose a known pending
          termination is grounds for decline regardless of other signals, and may be
          treated as application fraud.
        </P>

        <SectionHeading id="section-4-5" level={3}>4.5 Foreign Income Treatment</SectionHeading>
        <P>
          Income sourced from outside the Bank's primary jurisdiction is accepted with
          the following adjustments: the applicant must provide either six months of
          remittance evidence showing funds landing in a Bank-held or Bank-linked
          account, or tax documentation from the source jurisdiction. Foreign income
          is converted using the Bank's month-end exchange-rate table, and a 10%
          haircut is applied to the converted figure to account for exchange-rate
          volatility.
        </P>
        <P>
          Income denominated in currencies on the Bank's restricted list (published
          quarterly by Treasury) is not recognised for credit-decision purposes; the
          applicant must rely on other accepted income. Where foreign income is the
          dominant component (&gt; 60% of stated income), the case is routed to HITL
          regardless of other signals.
        </P>

        {/* ================================================================== */}
        {/* SECTION 5 — Credit Bureau Assessment */}
        {/* ================================================================== */}
        <SectionHeading id="section-5">5. Credit Bureau Assessment</SectionHeading>

        <SectionHeading id="section-5-1" level={3}>5.1 Bureau Selection and Freshness</SectionHeading>
        <P>
          The Bank pulls a credit bureau report from the primary bureau (Experian for
          US applicants, Equifax for Canadian applicants, the primary national bureau
          for all other jurisdictions) at the moment of application receipt. A
          secondary bureau may be pulled on request of a credit officer where the
          primary report is thin, flagged, or in dispute.
        </P>
        <P>
          Bureau reports are treated as fresh for 30 days from the pull date. A
          decision made more than 30 days after the pull requires a refreshed report.
          Where an application has been in <em>needs_info</em> status for more than 30
          days, a refreshed bureau pull is triggered automatically upon resumption.
        </P>

        <SectionHeading id="section-5-2" level={3}>5.2 Score Interpretation</SectionHeading>
        <P>
          The Bank uses the FICO 9 score for US applicants and equivalent
          jurisdiction-specific scores elsewhere. The Bureau Agent records the raw
          score along with the five principal contributing factors provided by the
          bureau (payment history, amounts owed, length of history, new credit, credit
          mix). Scores are interpreted as a single input to the tier calculation
          (Section 7) rather than a standalone accept/decline signal.
        </P>
        <P>
          Bureau-score bands used as inputs to the tier calculation:
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Band</Th>
              <Th>FICO 9 range</Th>
              <Th>Risk signal</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Prime</Td>
              <Td mono>740–850</Td>
              <Td>Strong; Tier A eligible</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Near-prime</Td>
              <Td mono>680–739</Td>
              <Td>Tier B eligible</Td>
            </tr>
            <tr>
              <Td>Sub-prime</Td>
              <Td mono>620–679</Td>
              <Td>Tier C cap</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Decline band</Td>
              <Td mono>&lt; 620</Td>
              <Td>Auto-decline on MIN_BUREAU_SCORE</Td>
            </tr>
          </tbody>
        </table>

        <SectionHeading id="section-5-3" level={3}>5.3 Tradelines, Delinquencies, Utilization</SectionHeading>
        <P>
          Beyond the headline score, the Bureau Agent records active tradelines,
          delinquencies within the last 24 months, and revolving-credit utilisation.
          Utilisation is computed as total revolving balances divided by total
          revolving credit limits across all reporting tradelines.
        </P>
        <P>
          A utilisation above 75% combined with a score below 680 triggers a
          <em> high-utilisation</em> warning on the case, which reduces the default
          tier by one level. Three or more 30-day-plus delinquencies in the last 12
          months trigger an <em>adverse-history</em> warning, which caps the applicant at
          Tier C. A single 60-day-plus delinquency in the last six months is
          sufficient to cap at Tier C.
        </P>

        <SectionHeading id="section-5-4" level={3}>5.4 Adverse Events and Collections</SectionHeading>
        <P>
          Active collections are a critical adverse signal. An applicant with any
          active collection account at the time of application is capped at Tier D
          (decline) unless the collection is (a) a medical-debt collection under the
          dollar threshold specified in the current CRB, or (b) a disputed item with
          documented evidence of dispute.
        </P>
        <P>
          Bankruptcies, judgments, and foreclosures are treated as follows:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>Bankruptcy (Chapter 7) &mdash; decline for 48 months from discharge.</li>
          <li>Bankruptcy (Chapter 13) &mdash; decline for 24 months from discharge; Tier C cap for the subsequent 24 months.</li>
          <li>Foreclosure &mdash; decline for 36 months from completion for mortgage products; Tier C cap for unsecured products.</li>
          <li>Tax lien &mdash; decline until released; Tier C cap for 12 months post-release.</li>
          <li>Civil judgment &mdash; case-by-case; typically Tier C cap for 12 months post-satisfaction.</li>
        </ul>

        <SectionHeading id="section-5-5" level={3}>5.5 Thin-file Applicants</SectionHeading>
        <P>
          Applicants with fewer than three tradelines or less than 24 months of
          reported credit history are considered thin-file. Thin-file status alone is
          not a basis for decline, but it removes Tier A eligibility. Thin-file
          applicants start at Tier B with strong profile indicators (stable
          employment, low DTI, verified income), Tier C otherwise.
        </P>
        <P>
          First-time borrowers (no credit history) may be approved on the Bank's
          Credit Builder programme, which is out of scope of this manual and governed
          by Supplemental Bulletin CB-2026-01. Standard unsecured products are not
          offered to applicants with no credit history.
        </P>
        <Callout type="note">
          A thin file in a specific <em>product</em> category &mdash; for example, a
          strong credit history with no prior auto loan &mdash; is a product-specific
          warning rather than a file-wide adverse signal. These cases often end up as
          HITL with the Analyst Agent surfacing the product-specific inexperience for
          officer consideration.
        </Callout>

        {/* ================================================================== */}
        {/* SECTION 6 — Affordability and DTI */}
        {/* ================================================================== */}
        <SectionHeading id="section-6">6. Affordability and DTI</SectionHeading>

        <SectionHeading id="section-6-1" level={3}>6.1 DTI Methodology</SectionHeading>
        <P>
          Debt-to-Income (DTI) is the ratio of the applicant's total monthly
          obligations to their gross monthly income. The Affordability Agent computes
          DTI using the verified income from the Income Verification Agent (Section
          4) and obligations from:
        </P>
        <ol className="list-decimal list-inside text-sm mb-3 space-y-1">
          <li>All instalment obligations reported on the bureau (auto, personal, student).</li>
          <li>Minimum monthly payments on all open revolving lines at the observed balances.</li>
          <li>Rent or mortgage payment (housing cost).</li>
          <li>Child-support or alimony obligations (if disclosed or visible in deposits).</li>
          <li>The <em>proposed</em> new monthly repayment.</li>
        </ol>
        <P>
          DTI is expressed as:
        </P>
        <pre className="bg-muted/50 border border-border rounded px-3 py-2 text-[0.72rem] mb-3 overflow-x-auto">
{`DTI = (existing_obligations + proposed_repayment) / gross_monthly_income`}
        </pre>
        <P>
          For variable-payment obligations (revolving cards where the observed
          payment differs from the minimum due), the Bank uses the greater of the
          actual observed monthly payment and the bureau-reported minimum due. This
          avoids under-counting obligations for applicants who pay substantially more
          than the minimum.
        </P>

        <SectionHeading id="section-6-2" level={3} anchor="RISK.DTI">
          6.2 DTI Bands and Actions
        </SectionHeading>
        <P>
          DTI is banded and translated into tier-impact actions as follows:
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>DTI band</Th>
              <Th>Action</Th>
              <Th>Discretion?</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td mono>&le; 35%</Td>
              <Td>No impact; all tiers eligible.</Td>
              <Td>N/A</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td mono>36–42%</Td>
              <Td>Warning; tier may drop by one.</Td>
              <Td>Eligible under compensating factors</Td>
            </tr>
            <tr>
              <Td mono>43–50%</Td>
              <Td>Tier C cap; officer discretion required to approve.</Td>
              <Td>Required</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td mono>&gt; 50%</Td>
              <Td>Hard decline; no discretion room.</Td>
              <Td>None</Td>
            </tr>
          </tbody>
        </table>
        <P>
          The 35% Tier A soft cap reflects the Bank's historical loss experience: DTI
          above 35% correlates materially with vintage-loss elevation for unsecured
          consumer products. The 42% Tier B soft cap provides headroom for borrowers
          with stronger compensating signals. The 50% hard ceiling is an absolute limit
          below which no retail credit may be extended, regardless of any other
          factor.
        </P>

        <SectionHeading id="section-6-3" level={3}>6.3 Stress Testing</SectionHeading>
        <P>
          For variable-rate products and secured lending, the Affordability Agent
          stress-tests the proposed repayment at a stressed rate &mdash; the rate
          currently in effect plus 200 basis points for variable-rate products, and
          plus 300 basis points for interest-only or payment-optional products. The
          stressed DTI must still satisfy the applicable tier cap; otherwise the
          product is not offered at the requested amount and a lower offer may be
          counter-proposed.
        </P>
        <P>
          Fixed-rate instalment products (fixed personal loans, fixed-rate mortgages)
          are not stress-tested on rate; however, a sensitivity check is applied for
          income shock by computing the DTI assuming a 15% income reduction. Cases
          that break the hard ceiling under this sensitivity are routed to HITL
          regardless of the nominal DTI.
        </P>

        <SectionHeading id="section-6-4" level={3}>6.4 Monthly Disposable Income Floor</SectionHeading>
        <P>
          In addition to DTI, the Bank enforces a minimum monthly disposable income
          floor, computed as gross income minus all obligations (including proposed
          repayment) minus the jurisdiction-specific minimum living-expense guideline
          published by Credit Risk Management.
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Household size</Th>
              <Th>Monthly living-expense floor (USD)</Th>
            </tr>
          </thead>
          <tbody>
            <tr><Td>1 adult</Td><Td mono>$1,650</Td></tr>
            <tr className="bg-muted/50"><Td>2 adults</Td><Td mono>$2,450</Td></tr>
            <tr><Td>2 adults, 1 child</Td><Td mono>$2,900</Td></tr>
            <tr className="bg-muted/50"><Td>2 adults, 2 children</Td><Td mono>$3,350</Td></tr>
            <tr><Td>Each additional dependant</Td><Td mono>+$400</Td></tr>
          </tbody>
        </table>
        <P>
          Applicants whose post-obligation disposable income falls below the
          household floor require HITL review, even where the DTI alone would permit
          automatic approval. The floors are reviewed annually and may be adjusted
          mid-year by CRB bulletin.
        </P>

        <SectionHeading id="section-6-5" level={3}>6.5 Student Loans and Revolving Credit</SectionHeading>
        <P>
          Federal student-loan obligations on income-driven repayment (IDR) plans are
          treated at the IDR-computed payment rather than the balance-amortising
          payment, where evidence of IDR enrolment is provided. Private student loans
          and standard-repayment federal loans use the bureau-reported minimum
          payment.
        </P>
        <P>
          Deferred student loans in grace period or deferment use 1% of the
          outstanding balance as the imputed monthly payment, reflecting likely
          post-deferment obligations. This treatment is conservative and protects
          applicants from post-grace payment shock as well as the Bank from
          under-counting obligations.
        </P>
        <P>
          For revolving credit, the Bank uses the greater of (a) 3% of the observed
          statement balance, and (b) the bureau-reported minimum due. This captures
          the minimum servicing cost for applicants carrying balances they cannot
          rapidly retire.
        </P>

        {/* ================================================================== */}
        {/* SECTION 7 — Risk Tiering */}
        {/* ================================================================== */}
        <SectionHeading id="section-7">7. Risk Tiering</SectionHeading>

        <SectionHeading id="section-7-1" level={3} anchor="RISK.TIERING">
          7.1 Tier Definitions A–D
        </SectionHeading>
        <P>
          Applicants are placed into one of four risk tiers, derived from the
          composite of credit-bureau score, debt-to-income ratio, employment tenure,
          and affordability. The tiers determine pricing, amount caps, and
          auto-decisioning eligibility. Tier placement is computed by the Analyst
          Agent after all Analysis-stage findings are synthesised.
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Tier</Th>
              <Th>Label</Th>
              <Th>Bureau</Th>
              <Th>DTI</Th>
              <Th>Tenure</Th>
              <Th>Auto-decision</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td center><strong>A</strong></Td>
              <Td>Prime</Td>
              <Td mono>&ge; 740</Td>
              <Td mono>&le; 35%</Td>
              <Td mono>&ge; 2 yrs</Td>
              <Td>Auto-approve eligible</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td center><strong>B</strong></Td>
              <Td>Near-prime</Td>
              <Td mono>680–739</Td>
              <Td mono>&le; 42%</Td>
              <Td mono>&ge; 1 yr</Td>
              <Td>HITL (officer review)</Td>
            </tr>
            <tr>
              <Td center><strong>C</strong></Td>
              <Td>Sub-prime</Td>
              <Td mono>620–679</Td>
              <Td mono>43–50%</Td>
              <Td mono>&lt; 1 yr permitted</Td>
              <Td>HITL with discretion</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td center><strong>D</strong></Td>
              <Td>Decline</Td>
              <Td mono>&lt; 620</Td>
              <Td mono>&gt; 50%</Td>
              <Td mono>&mdash;</Td>
              <Td>Auto-decline</Td>
            </tr>
          </tbody>
        </table>
        <P>
          An applicant is placed in the worst tier their individual signals permit.
          An applicant with a 790 bureau score and 44% DTI is placed at Tier C (the
          DTI cap) even though the bureau score alone would qualify for Tier A. This
          “worst-of” rule ensures the Bank's aggregate risk exposure does not
          accumulate across ostensibly compensating signals.
        </P>

        <SectionHeading id="section-7-2" level={3}>7.2 Composite Scoring</SectionHeading>
        <P>
          The tier assignment combines four signals with the following weights in
          the Bank's composite risk score: bureau score (45%), DTI (30%), affordability
          margin (15%), and employment tenure (10%). The composite is computed for
          informational and model-monitoring purposes; the tier placement itself
          follows the worst-of rule described above, not the composite weight.
        </P>
        <P>
          The composite is also used to set within-tier pricing. Two Tier B applicants
          may receive different interest-rate offers depending on where within the
          tier their composite sits. Pricing tables are maintained by Product &amp;
          Pricing and are outside the scope of this manual.
        </P>

        <SectionHeading id="section-7-3" level={3}>7.3 Tier Drift</SectionHeading>
        <P>
          A small number of signals cause the applicant's tier to drift by one level
          regardless of the primary inputs. Tier drift is always in the adverse
          direction (B&rarr;C, not C&rarr;B). Drift conditions:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>Revolving utilisation &gt; 75% combined with bureau &lt; 680 (Section 5.3).</li>
          <li>Any 60-day-plus delinquency in the last six months.</li>
          <li>Income variance &gt; 15% between stated and bank-deposit-derived (Section 4.1).</li>
          <li>
            Product-specific thin file &mdash; for example, first-time auto-loan
            borrower on an auto-loan application. Analyst Agent surfaces as warning.
          </li>
        </ul>
        <P>
          Tier drift from any of the above triggers an Analyst Agent finding that is
          surfaced to officers and to the Decision Agent. Where drift causes the
          tier to cross the auto-approve boundary (Tier A&rarr;B), the application is
          automatically routed to HITL.
        </P>

        <SectionHeading id="section-7-4" level={3}>7.4 Override Procedures</SectionHeading>
        <P>
          Officers may upgrade or downgrade the computed tier by one level, provided
          the rationale is documented and the new tier's thresholds are satisfied by
          the applicant's signals. Upgrades require written justification and must be
          approved by an officer one authority level above the upgrade-making officer.
          Downgrades (protective of the Bank) may be made unilaterally.
        </P>
        <P>
          Two-level tier overrides (for example, forcing Tier C to Tier A) are not
          permitted under any circumstances. Agentic systems may not adjust tier;
          tier adjustments are exclusively human-officer actions.
        </P>

        {/* ================================================================== */}
        {/* SECTION 8 — Product-specific Policies */}
        {/* ================================================================== */}
        <SectionHeading id="section-8">8. Product-specific Policies</SectionHeading>

        <SectionHeading id="section-8-1" level={3} anchor="POLICY.AMOUNT">
          8.1 Amount Caps by Tier
        </SectionHeading>
        <P>
          The following caps apply to new-origination unsecured and small-secured
          lending. Cap-exceeding requests may be counter-offered at the cap amount
          and approved on that basis.
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Tier</Th>
              <Th>Personal Loan</Th>
              <Th>Credit Card limit</Th>
              <Th>Auto Loan</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td center><strong>A</strong></Td>
              <Td mono>up to $75,000</Td>
              <Td mono>up to $40,000</Td>
              <Td mono>up to $60,000</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td center><strong>B</strong></Td>
              <Td mono>up to $45,000</Td>
              <Td mono>up to $20,000</Td>
              <Td mono>up to $45,000</Td>
            </tr>
            <tr>
              <Td center><strong>C</strong></Td>
              <Td mono>up to $20,000</Td>
              <Td mono>up to $8,000</Td>
              <Td mono>up to $20,000</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td center><strong>D</strong></Td>
              <Td mono>not eligible</Td>
              <Td mono>not eligible</Td>
              <Td mono>not eligible</Td>
            </tr>
          </tbody>
        </table>
        <P>
          Officer discretion may approve up to 10% above the tier cap, provided (a)
          the justification is recorded in the audit log, (b) DTI remains &le; 50%
          under the stress-tested repayment at the higher amount, and (c) the officer
          has authority equal to or above the cap-exceeding amount. Discretion
          overrides are reviewed quarterly by the CRMC; a pattern of over-cap approvals
          by any single officer triggers re-authorisation review.
        </P>

        <SectionHeading id="section-8-2" level={3}>8.2 Personal Loans</SectionHeading>
        <P>
          Unsecured personal loans are available at terms of 12, 24, 36, 48, and 60
          months. The 60-month term is available only to Tier A and Tier B applicants,
          and only for purposes declared in the application as debt consolidation,
          home improvement, or major purchase. General-purpose loans are capped at 48
          months.
        </P>
        <P>
          Loan proceeds declared as “business use” are not permitted under this
          manual; such applications must be redirected to the Small Business Unit and
          underwritten against the Commercial Credit Manual. Declared purposes of
          “refinance an existing Bank loan” are permitted only where the existing
          facility has 18 or more on-time payments; earlier refinances are typically
          flagged as reserve-deferral behaviour.
        </P>

        <SectionHeading id="section-8-3" level={3}>8.3 Credit Cards</SectionHeading>
        <P>
          Credit-card limits follow the tier caps in Section 8.1, with the following
          refinements: premium card products are restricted to Tier A applicants and
          require a minimum bureau score of 760. Secured-card products may be offered
          at Tier C, Tier D, or thin-file applicants on deposit of a security balance
          equal to the requested limit; secured-card origination does not require
          income verification but does require identity, address, and fraud screening.
        </P>
        <P>
          Credit-line increases (CLI) on existing accounts follow a lighter process
          than origination but still require verification of recent income and a
          refreshed bureau pull. Unsolicited CLIs are made only to Tier A and B
          cardholders with at least 12 months on book and no adverse events in the
          last 6 months.
        </P>

        <SectionHeading id="section-8-4" level={3}>8.4 Auto Loans</SectionHeading>
        <P>
          Auto loans are secured by the financed vehicle; the Bank records a lien
          against the title and requires proof of comprehensive-and-collision
          insurance naming the Bank as loss payee. Maximum terms are 60 months for new
          vehicles and 48 months for used vehicles (older than 4 model years).
        </P>
        <P>
          Loan-to-Value (LTV) is computed against the lesser of the vehicle purchase
          price and the Bank's approved valuation source (currently KBB Retail for US
          applicants). Maximum LTV is 120% for new vehicles and 110% for used
          vehicles; LTV above these caps requires an additional 5% down payment or
          counter-offer at the maximum permitted loan amount.
        </P>
        <Callout type="warning">
          <strong>Electric-vehicle financing.</strong> New-model-year EV purchases
          carry higher residual-value variance than comparable ICE vehicles owing to
          rapid battery and powertrain technology cycles. The Analyst Agent must
          surface an EV residual-value consideration for any new-EV financing, and
          the term must be set such that projected LTV stays within policy through at
          least month 24, based on the Bank's quarterly EV residual-value table.
        </Callout>

        <SectionHeading id="section-8-5" level={3}>8.5 Mortgages and HELOCs</SectionHeading>
        <P>
          Mortgage and HELOC underwriting is subject to this manual and to the
          Mortgage Underwriting Manual (MU-2026-002); where the two conflict, the
          Mortgage Manual governs. Standard eligibility: Tier A and Tier B only for
          owner-occupied primary residences; Tier A only for investment-property
          loans. Cash-out refinances require LTV &le; 80% and a minimum seasoning of
          12 months on the existing mortgage.
        </P>
        <P>
          HELOC applications require income verification and employment tenure
          requirements consistent with unsecured products. The combined-LTV (CLTV)
          ceiling is 85% for primary residences and 75% for second homes. HELOCs
          secured against investment properties are governed by the Mortgage Manual
          and are outside the scope of this document.
        </P>

        {/* ================================================================== */}
        {/* SECTION 9 — Fraud Screening */}
        {/* ================================================================== */}
        <SectionHeading id="section-9">9. Fraud Screening</SectionHeading>

        <SectionHeading id="section-9-1" level={3} anchor="RISK.FRAUD">
          9.1 Fraud Signal Taxonomy
        </SectionHeading>
        <P>
          Applications are screened for device-fingerprint anomalies, application
          velocity (more than three applications in 30 days), and known-pattern
          matches against the Bank's internal fraud ring database. Each signal is
          rated <em>none</em>, <em>info</em>, <em>warn</em>, or <em>critical</em>. Any
          critical fraud signal routes the application to the Fraud Investigation
          Team (<em>fraud_hold</em>); a fraud-hold application may not be auto-approved
          under any circumstances, regardless of other factors.
        </P>
        <P>
          The four severity levels:
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Severity</Th>
              <Th>Typical signals</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>None</Td>
              <Td>Device seen 1x/12mo, no velocity hits, no pattern matches</Td>
              <Td>Pass</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Info</Td>
              <Td>Device first-seen within 30 days; low-signal IP reputation</Td>
              <Td>Pass; recorded for future reference</Td>
            </tr>
            <tr>
              <Td>Warn</Td>
              <Td>Device seen 3–6x/48h; two applications in 30 days; soft-pattern</Td>
              <Td>HITL route; officer reviews</Td>
            </tr>
            <tr className="bg-muted/50">
              <Td>Critical</Td>
              <Td>Known fraud ring; velocity &gt; 6/48h; stolen-credential match</Td>
              <Td>Immediate fraud_hold; no credit decision communicated</Td>
            </tr>
          </tbody>
        </table>

        <SectionHeading id="section-9-2" level={3}>9.2 Device Fingerprinting</SectionHeading>
        <P>
          The Bank captures a device fingerprint at every application touchpoint: the
          combination of browser user-agent, screen geometry, installed fonts, canvas
          fingerprint, timezone, and other signals the privacy policy permits. The
          fingerprint is hashed and compared against the Bank's 12-month lookup table.
        </P>
        <P>
          A device seen repeatedly against different applicant identities is a strong
          fraud indicator. Thresholds:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>Device fingerprint seen &gt; 1 time in 12 months against different identities → <em>warn</em>.</li>
          <li>Device fingerprint seen &gt; 3 times in 48 hours against any identities → <em>warn</em>.</li>
          <li>Device fingerprint on the Bank's confirmed-fraud deny-list → <em>critical</em>.</li>
        </ul>

        <SectionHeading id="section-9-3" level={3}>9.3 Velocity and Known-pattern Rules</SectionHeading>
        <P>
          Application velocity measures the number of applications from a given
          identity or device within a rolling window. The Bank deems the following
          velocities adverse:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>&gt; 3 applications in 30 days from a single identity → <em>warn</em>.</li>
          <li>&gt; 1 application in 24 hours from a single device → <em>warn</em>.</li>
          <li>&gt; 6 applications in 48 hours from a single device or IP → <em>critical</em>.</li>
        </ul>
        <P>
          Known-pattern matching compares the application against a library of
          historical fraud cases curated by the Fraud Investigation Team. Patterns
          include known deny-list identities, synthetic-identity signatures (new SSN
          paired with deep credit history), and known device-farm fingerprints. A
          match against the curated library is automatically <em>critical</em>.
        </P>

        <SectionHeading id="section-9-4" level={3}>9.4 Fraud Hold Workflow</SectionHeading>
        <P>
          When an application is placed on <em>fraud_hold</em>, the Decision Agent
          does not render a credit decision. Instead, the case is referred to the
          Fraud Investigation Team, which has a separate workflow outside the credit
          queue. The applicant is notified only after the Fraud team has completed
          its investigation, and only in terms approved by the Legal team.
        </P>
        <P>
          Applications cleared by the Fraud team are returned to the credit queue
          with a fraud-cleared stamp and re-evaluated at the point of re-entry. A
          cleared case may be auto-approved if all other criteria are satisfied; the
          fraud-cleared status is noted on the case. Applications confirmed fraudulent
          are closed and an adverse-action notice is issued per FCRA/Reg B rules.
        </P>

        {/* ================================================================== */}
        {/* SECTION 10 — Decisioning, HITL, and Audit */}
        {/* ================================================================== */}
        <SectionHeading id="section-10">10. Decisioning, HITL, and Audit</SectionHeading>

        <SectionHeading id="section-10-1" level={3} anchor="DECISION.AUTO">
          10.1 Auto-approve Criteria
        </SectionHeading>
        <P>
          An application auto-approves only when <strong>all</strong> of the following
          hold simultaneously:
        </P>
        <ol className="list-decimal list-inside text-sm mb-3 space-y-1">
          <li>Risk Tier is <strong>A</strong> (Section 7.1).</li>
          <li>Requested amount is within the applicable Tier A cap (Section 8.1).</li>
          <li>All policy evaluations return <em>pass</em>; none return <em>warn</em> or <em>fail</em>.</li>
          <li>No active fraud signals of severity <em>warn</em> or <em>critical</em>.</li>
          <li>Identity, address, and income have all been verified without mismatch.</li>
          <li>Document completeness is satisfied (no outstanding requirements).</li>
        </ol>
        <P>
          The Decision Agent emits <code>decision:auto_approved</code> to the audit
          log along with the specific reasoning citing each rule that contributed.
          The applicant is notified immediately, and the funding / fulfilment workflow
          is initiated automatically.
        </P>

        <SectionHeading id="section-10-2" level={3} anchor="DECISION.AUTO">
          10.2 Auto-decline Criteria
        </SectionHeading>
        <P>
          An application auto-declines when <strong>any</strong> of the following
          hard-fail conditions is met:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>Bureau score &lt; 620 (MIN_BUREAU_SCORE hard-fail).</li>
          <li>DTI &gt; 50% (hard ceiling; Section 6.2).</li>
          <li>Tier D placement for any reason (Section 7.1).</li>
          <li>Missing mandatory identity evidence (Section 3.1) after needs_info window has closed.</li>
          <li>Confirmed fraud disposition from the Fraud Investigation Team.</li>
        </ul>
        <P>
          The Decision Agent emits <code>decision:auto_declined</code> with a
          reasoning statement citing the specific hard-fail rule(s). An
          adverse-action notice is generated automatically and issued within the
          applicable regulatory window (30 days for ECOA/Reg B).
        </P>
        <Callout type="important">
          Auto-decline is reserved for conditions where no reasonable officer could
          approve; it exists to reduce officer load on clear-cut cases, not to remove
          officer judgement. Every auto-decline is sampled quarterly by QA to confirm
          the hard-fail rules fired correctly and no discretion-eligible applicant was
          auto-declined.
        </Callout>

        <SectionHeading id="section-10-3" level={3} anchor="DECISION.HITL">
          10.3 Human-in-the-loop
        </SectionHeading>
        <P>
          Any application that is neither auto-approve nor auto-decline is routed to
          the Review Queue for human-in-the-loop (HITL) officer review. The assigned
          officer must:
        </P>
        <ol className="list-decimal list-inside text-sm mb-3 space-y-1">
          <li>Review the extracted application data for completeness and accuracy.</li>
          <li>Read the Analyst Agent's findings and assess each one in context.</li>
          <li>Review each policy evaluation from the Compliance Agent, noting any discretion-eligible flags.</li>
          <li>Decide: approve, approve with recorded discretion, decline, or request additional information.</li>
        </ol>
        <P>
          HITL is the mechanism by which the Bank applies judgement to applications
          whose profile is ambiguous, borderline, or otherwise unsuited to
          deterministic rules. It is not a rubber-stamp gate but a substantive
          review. The officer is expected to spend the time needed to reach a
          confident decision; quick-approval of obvious cases is appropriate, but
          every HITL decision must be traceable to a documented rationale.
        </P>

        <SectionHeading id="section-10-4" level={3}>10.4 Officer Discretion</SectionHeading>
        <P>
          Officer discretion is the authority to approve an application outside
          strict policy bounds, provided the rationale is documented and the
          discretion-eligibility flags from the Compliance Agent permit. Discretion
          is bounded &mdash; it does not unlock all hard-fail rules, and is explicitly
          excluded from certain policies (fraud critical, DTI hard ceiling, Tier D
          placement, missing mandatory ID).
        </P>
        <P>
          A discretion-approval must include a written justification in the officer's
          own words, citing the specific compensating factors. The justification is
          appended to the audit log and is reviewed by the CRMC's Quality Assurance
          team on a sample basis. Officers whose discretion-approvals result in
          materially higher vintage losses than peers are subject to re-authorisation
          review.
        </P>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Policy</Th>
              <Th>Discretion eligible?</Th>
              <Th>Bounds</Th>
            </tr>
          </thead>
          <tbody>
            <tr><Td>DTI 36–42%</Td><Td>Yes</Td><Td>Compensating factors required</Td></tr>
            <tr className="bg-muted/50"><Td>DTI 43–50%</Td><Td>Yes</Td><Td>Officer justification + supervisor sign-off</Td></tr>
            <tr><Td>DTI &gt; 50%</Td><Td><strong>No</strong></Td><Td>Hard ceiling</Td></tr>
            <tr className="bg-muted/50"><Td>Amount 0–10% over tier cap</Td><Td>Yes</Td><Td>Justification + DTI &le; 50%</Td></tr>
            <tr><Td>Amount &gt; 10% over tier cap</Td><Td><strong>No</strong></Td><Td>Counter-offer at cap</Td></tr>
            <tr className="bg-muted/50"><Td>Tenure &lt; 1 year</Td><Td>Yes</Td><Td>Bureau &ge; 720 + stable history</Td></tr>
            <tr><Td>Bureau &lt; 620</Td><Td><strong>No</strong></Td><Td>Hard fail</Td></tr>
            <tr className="bg-muted/50"><Td>Fraud critical</Td><Td><strong>No</strong></Td><Td>Fraud_hold workflow</Td></tr>
          </tbody>
        </table>

        <SectionHeading id="section-10-5" level={3}>10.5 Audit Trail</SectionHeading>
        <P>
          Every credit decision, whether automated or human, generates an audit
          record capturing: the actor (officer ID or agent identifier), the action,
          the timestamp, the citation anchors from this manual that were applied, and
          any supporting justification text. Audit records are immutable; corrections
          are captured as supplementary entries, never by modifying historical
          entries.
        </P>
        <P>
          Each of the agents in the BAIC Agentic Layer emits a structured audit entry
          at the conclusion of its work. For example, the Bureau Agent records a
          <code>analysis:bureau_pulled</code> action with the retrieved score,
          delinquency count, and utilisation. The Decision Agent records
          <code>decision:auto_approved</code>, <code>decision:auto_declined</code>, or
          <code>decision:recommended</code> depending on the routing outcome. Officer
          actions such as approve-with-discretion append
          <code>decision:approve_with_discretion</code> with the written justification.
        </P>
        <P>
          Audit records are retained for a minimum of seven years from the date of
          the credit decision, or longer where applicable retention rules require.
          The audit log is searchable by applicant, officer, agent, and action type
          through the Governance dashboard.
        </P>

        <SectionHeading id="section-10-6" level={3}>10.6 Quality Assurance</SectionHeading>
        <P>
          Credit Risk Management samples decisions quarterly for QA review. Sample
          sizes and stratification:
        </P>
        <ul className="list-disc list-inside text-sm mb-3 space-y-1">
          <li>5% of all auto-approved cases (stratified by amount and product).</li>
          <li>10% of all auto-declined cases.</li>
          <li>20% of all discretion-approved cases.</li>
          <li>100% of cases approved outside explicit policy (requires pre-approval, exceptional).</li>
        </ul>
        <P>
          QA reviewers assess whether the decision is supported by the documented
          evidence, whether the cited policies were applied correctly, and whether
          the officer's (or agent's) rationale is consistent with the manual. QA
          findings are categorised as <em>pass</em>, <em>teachable moment</em>,
          <em>policy gap</em>, or <em>material defect</em>. Material defects are
          escalated to the CRMC and may result in individual or team re-authorisation.
        </P>

        <SectionHeading id="section-10-7" level={3}>Appendix A: Glossary</SectionHeading>
        <P>
          Terms used throughout this manual:
        </P>
        <dl className="text-sm space-y-2 mb-4">
          <div><dt className="font-semibold inline">Agent.</dt>{" "}<dd className="inline">An automated decisioning component within the BAIC Agentic Layer; may be a unitary agent (Bureau, Reader, etc.) or a supervisor agent.</dd></div>
          <div><dt className="font-semibold inline">Bureau.</dt>{" "}<dd className="inline">A consumer credit reporting agency (Experian, Equifax, TransUnion) that aggregates credit history and produces a creditworthiness score.</dd></div>
          <div><dt className="font-semibold inline">Compensating factor.</dt>{" "}<dd className="inline">A positive signal that offsets an adverse signal, supporting approval despite a borderline metric.</dd></div>
          <div><dt className="font-semibold inline">DTI.</dt>{" "}<dd className="inline">Debt-to-Income ratio. See Section 6.</dd></div>
          <div><dt className="font-semibold inline">HITL.</dt>{" "}<dd className="inline">Human-in-the-loop; a workflow where agentic decisioning defers to a credit officer for the final decision.</dd></div>
          <div><dt className="font-semibold inline">LTV.</dt>{" "}<dd className="inline">Loan-to-Value ratio, typically for secured products. Loan amount divided by the value of the pledged asset.</dd></div>
          <div><dt className="font-semibold inline">needs_info.</dt>{" "}<dd className="inline">Application status indicating that required information is missing; the application is paused pending applicant response.</dd></div>
          <div><dt className="font-semibold inline">Officer discretion.</dt>{" "}<dd className="inline">Authority of a credit officer to approve or decline an application within a bounded range defined by policy, with written justification.</dd></div>
          <div><dt className="font-semibold inline">PEP.</dt>{" "}<dd className="inline">Politically Exposed Person. Subject to enhanced due diligence.</dd></div>
          <div><dt className="font-semibold inline">Residual value.</dt>{" "}<dd className="inline">Estimated market value of a financed asset at a future point in the loan term. Relevant for secured lending LTV over time.</dd></div>
          <div><dt className="font-semibold inline">Soft cap / Hard cap.</dt>{" "}<dd className="inline">Soft cap = preferred threshold with discretion permitted above; hard cap = absolute ceiling with no discretion room.</dd></div>
          <div><dt className="font-semibold inline">Thin file.</dt>{" "}<dd className="inline">Limited credit history, fewer than three tradelines or less than 24 months reported.</dd></div>
          <div><dt className="font-semibold inline">Tier.</dt>{" "}<dd className="inline">Internal risk band (A–D) combining bureau score, DTI, tenure, and affordability.</dd></div>
        </dl>

        <SectionHeading id="section-10-8" level={3}>Appendix B: Escalation Contacts</SectionHeading>
        <table className="w-full text-xs border-collapse border border-border mb-4">
          <thead>
            <tr className="bg-muted">
              <Th>Topic</Th>
              <Th>Contact</Th>
              <Th>SLA</Th>
            </tr>
          </thead>
          <tbody>
            <tr><Td>Policy interpretation</Td><Td>Credit Policy Desk (policy@bank.internal)</Td><Td>1 business day</Td></tr>
            <tr className="bg-muted/50"><Td>Fraud disposition</Td><Td>Fraud Investigation Team (fraud@bank.internal)</Td><Td>Same day</Td></tr>
            <tr><Td>Discretion-approval sign-off</Td><Td>Head of Underwriting (direct)</Td><Td>Same day</Td></tr>
            <tr className="bg-muted/50"><Td>AML/KYC escalation</Td><Td>Financial Crimes Compliance (fcc@bank.internal)</Td><Td>1 business day</Td></tr>
            <tr><Td>Agentic-system anomaly</Td><Td>Credit AI Ops (credit-ai@bank.internal)</Td><Td>4 hours</Td></tr>
            <tr className="bg-muted/50"><Td>QA finding dispute</Td><Td>Credit Risk Management Committee</Td><Td>10 business days</Td></tr>
          </tbody>
        </table>

        <div className="mt-12 border-t border-border pt-4 text-xs text-muted-foreground">
          <p><strong>End of Credit Policy Manual CP-MANUAL-2026-007 v11.4.</strong></p>
          <p className="mt-1">
            Questions or proposed amendments: Credit Policy Desk
            (policy@bank.internal). Amendments take effect on the quarterly
            release cycle unless flagged as emergency by the CRMC.
          </p>
        </div>
      </article>
    </div>
  );
}
